/**
 * Results Page Types
 * Defines all interfaces related to test results and analysis
 */

export interface DimensionScoreData {
  score: number;
  max_score: number;
  percentage: number;
  instruction_time: number;
}

export interface InstructionDetail {
  section_id: string;
  section_name: string;
  time_spent: number;
  time_limit: number | null;
  viewed_at: string | null;
}

export interface AnswerDetail {
  question_id: string;
  user_answer: string;
  correct_answer: string;
  is_correct: boolean;
  points_earned: number;
  time_taken: number;
  time_limit: number;
  pre_rule_time: number;
  distraction_time: number;
  distraction_moves: number;
  answered_at: string | null;
}

export interface ResultData {
  attempt_id: number;
  version: string;
  total_score: number;
  max_score: number;
  percentage: number;
  total_time_seconds: number;
  total_time_formatted: string;
  status: string;
  completed_at: string | null;
  started_at: string | null;
  answers: AnswerDetail[];
  dimension_scores: Record<string, DimensionScoreData>;
  instruction_times: InstructionDetail[];
  total_questions: number;
  correct_answers: number;
  incorrect_answers: number;
  skipped_answers: number;
  total_answer_time: number;
  total_instruction_time: number;
  total_pre_rule_time: number;
  total_distraction_time: number;
  total_distraction_moves: number;
  average_answer_time: number;
  profile_analysis?: string | null;
  // New separate profile and recommendations
  profile_report?: ProfileReportData | null;
  recommendations?: RecommendationsData | null;
}

/**
 * Profile Report Data (instant, no LLM)
 */
export interface ProfileReportData {
  attempt_id: number;
  profile_id: number;
  profile_name: string;
  subtitle: string;
  category: string;
  confidence: number;
  average_score: number;
  population_percentage: string;
  strong_dims: string[];
  weak_dims: string[];
  description: string;
  cognitive_functioning: string;
  metaphor: string;
  celebrities: Array<{name: string; domain: string; link: string}>;
  dimensions: Record<string, {score: number; percentile: number; level: string}>;
  forces: {forces: Array<any>};
  challenges: {points_vigilance: Array<any>};
  dev_plan: {plan_developpement_6_mois: any; evolution_possible: string};
  narrative: {
    environnements_ideaux: any;
    pattern_comportemental: any;
    valorisation_entretien: any;
  };
  valorisation: {
    personal_branding: any;
    missions_projets: any;
    negociation: any;
    ressources_developpement: any[];
    message_final: any;
  };
  formatted_report: string;
}

/**
 * Recommendations Data (LLM-generated)
 */
export interface RecommendationsData {
  attempt_id: number;
  profile_name: string;
  recommendations: {
    exploiter_forces: {
      introduction: string;
      strategies: Array<{
        titre: string;
        description: string;
        action_immediate: string;
      }>;
      pieges_a_eviter: string[];
    };
    developper_faiblesses: {
      introduction: string;
      strategies: Array<{
        faiblesse: string;
        strategie: string;
        exercice_quotidien: string;
        indicateur_progres: string;
      }>;
      conseil_mindset: string;
    };
    conseils_carriere: {
      orientations_ideales: string[];
      erreurs_classiques: string[];
      plan_action: {
        court_terme: string;
        moyen_terme: string;
        long_terme: string;
      };
      message_motivation: string;
    };
  };
  from_cache: boolean;
}

export type TabType = 'summary' | 'dimensions' | 'answers' | 'times' | 'profile' | 'recommendations';

export const DIMENSION_LABELS: Record<string, string> = {
  'vitesse_acquisition': "Vitesse d'acquisition",
  'vitesse': "Vitesse d'acquisition",
  'retention': 'Retention',
  'transfert': 'Transfert',
  'adaptabilite': 'Adaptabilite',
  'feedback': 'Feedback'
};

export const DIMENSION_TO_SECTION: Record<string, string> = {
  'vitesse_acquisition': 'vitesse',
  'vitesse': 'vitesse',
  'retention': 'retention',
  'transfert': 'transfert',
  'adaptabilite': 'adaptabilite',
  'feedback': 'feedback'
};

/**
 * Get question number range for a dimension
 */
export const getDimensionQuestionRange = (dimName: string): [number, number] => {
  switch (dimName) {
    case 'vitesse_acquisition':
    case 'vitesse':
      return [1, 10];
    case 'retention':
      return [11, 18];
    case 'transfert':
      return [19, 26];
    case 'adaptabilite':
      return [27, 36];
    case 'feedback':
      return [37, 44];
    default:
      return [0, 0];
  }
};
